import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CalendarIcon, Loader2, Pencil } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const FIELDS = [
  { key: "vat_rate", label: "Taux TVA", type: "number" },
  { key: "city", label: "Ville", type: "text" },
  { key: "postal_code", label: "Code postal", type: "text" },
  { key: "status", label: "Statut", type: "select", options: [
    { value: "pending", label: "En attente" },
    { value: "planning", label: "Au planning" },
    { value: "in_progress", label: "En cours" },
    { value: "completed", label: "Terminée" },
    { value: "cashed", label: "Encaissée" },
    { value: "cancelled", label: "Annulée" },
  ]},
  { key: "completed_at", label: "Date de complétion", type: "date" },
];

/**
 * Dialog de modification en masse des commandes sélectionnées.
 * Props: { open, onClose, selectedOrderIds, sellers, orderTypes, onUpdated }
 */
export const BulkEditOrdersDialog = ({ open, onClose, selectedOrderIds, sellers = [], orderTypes = [], onUpdated }) => {
  const [enabledFields, setEnabledFields] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setEnabledFields({});
    setValues({});
  };

  const close = () => {
    reset();
    onClose();
  };

  const allFields = [
    ...FIELDS,
    { key: "order_type_id", label: "Type de commande", type: "select", options: orderTypes.map((t) => ({ value: t.id, label: t.name })) },
    { key: "seller_id", label: "Vendeur", type: "select", options: [
      { value: "__none__", label: "Aucun" },
      ...sellers.map((s) => ({ value: s.id, label: s.name })),
    ]},
  ];

  const toggleField = (key) => {
    setEnabledFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setValue = (key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const handleApply = async () => {
    const fields = {};
    for (const f of allFields) {
      if (!enabledFields[f.key]) continue;
      let val = values[f.key];
      if (val === undefined || val === "") continue;
      if (f.key === "vat_rate") val = parseFloat(val);
      if (f.key === "completed_at" && val instanceof Date) val = val.toISOString();
      if (f.key === "seller_id" && val === "__none__") val = null;
      fields[f.key] = val;
    }
    if (Object.keys(fields).length === 0) {
      toast.error("Cochez au moins un champ et renseignez sa valeur");
      return;
    }
    setSaving(true);
    try {
      const res = await api.bulkUpdateOrders({ order_ids: selectedOrderIds, fields });
      toast.success(`${res.data.updated} commande(s) modifiée(s)`);
      if (onUpdated) onUpdated();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    if (f.type === "select") {
      return (
        <Select value={values[f.key] || ""} onValueChange={(v) => setValue(f.key, v)}>
          <SelectTrigger data-testid={`bulk-edit-${f.key}`}>
            <SelectValue placeholder="Choisir..." />
          </SelectTrigger>
          <SelectContent>
            {f.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "date") {
      const d = values[f.key];
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid={`bulk-edit-${f.key}`}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {d ? format(d, "dd MMMM yyyy", { locale: fr }) : "Sélectionner une date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={d} onSelect={(v) => setValue(f.key, v)} locale={fr} initialFocus />
          </PopoverContent>
        </Popover>
      );
    }
    return (
      <Input
        type={f.type}
        value={values[f.key] ?? ""}
        onChange={(e) => setValue(f.key, e.target.value)}
        placeholder={f.label}
        data-testid={`bulk-edit-${f.key}`}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bulk-edit-orders-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            Modifier {selectedOrderIds.length} commande{selectedOrderIds.length > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            Cochez les champs à modifier. Les valeurs existantes des commandes sélectionnées seront écrasées.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {allFields.map((f) => (
            <div key={f.key} className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-5 flex items-center gap-2">
                <Checkbox
                  id={`bulk-${f.key}`}
                  checked={!!enabledFields[f.key]}
                  onCheckedChange={() => toggleField(f.key)}
                  data-testid={`bulk-edit-toggle-${f.key}`}
                />
                <Label htmlFor={`bulk-${f.key}`} className="cursor-pointer">{f.label}</Label>
              </div>
              <div className={`col-span-7 ${enabledFields[f.key] ? "" : "opacity-40 pointer-events-none"}`}>
                {renderField(f)}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={saving}>Annuler</Button>
          <Button onClick={handleApply} disabled={saving} data-testid="bulk-edit-apply">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEditOrdersDialog;
